import React, { Fragment } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import {
  PageHeader,
  SimpleDataTableWithButtons,
  Section,
  DangerousWrapper,
  makeBreadcrumb,
  slugify,
  toCurrency,
  GatsbyLink
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import PlanFilter from '../components/PlanFilter';
import './investment-by-subject.scss';

const InvestmentSubject = (props) => {
  const { data, location } = props;
  const { content, site } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent.parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, '');
  const { baseSectionURL, budgetSection, title } = content;
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: baseSectionURL,
    text: budgetSection,
    sections: [title]
  });
  const layoutProps = {
    activeIndex: 1,
    pre: () => <PlanFilter path={relPath} />,
    above: () => <PageHeader title={pageTitle} category={budgetSection} />,
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime,
    containerClasses: 'main-content main-content--two'
  };
  const getSubComponent = (row, columns) => (
    <Fragment>
      {row.original.planItems.map((item, index) => {
        const planItemLink = `/beneficiary-agency/${slugify(item.govLevelOne)}/${slugify(item.govLevelTwo)}/${slugify(item.planItemNumber)}`;
        const dataKeys = item.data && Object.keys(item.data);
        return(
          // Suppress plan item with "suppressed" flag
          // eslint-disable-next-line react/no-array-index-key
          <div className={`rt-tr ${item.suppressed ? 'suppressed' : ''}`} role="row" key={`planitem-${item.planItemNumber}-${index}`}>
            <div className="rt-td rt-expandable" style={{ flex: '35 0 auto', width: '35px', maxWidth: '35px' }} />
            <div className={`rt-td ${columns[0].className}`} style={{ flex: `${columns[0].minWidth} 0 auto`, width: `${columns[0].minWidth}px` }}>
              <GatsbyLink to={planItemLink}>{item.planItemNumber}</GatsbyLink>
              <DangerousWrapper rawHtml={item.planItemName} htmlTag="span" className="plan-item-name" />
            </div>
            {
              dataKeys && dataKeys.map((record, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <DangerousWrapper key={`wrapper-${columns[i + 1].className}-${index}-${i}`} rawHtml={toCurrency(item.data[record])} htmlTag="div" className={`rt-td ${columns[i + 1].className}`} style={{ flex: `${columns[i + 1].minWidth} 0 auto`, width: `${columns[i + 1].minWidth}px` }} />
              ))
            }
          </div>
        );
      })}
    </Fragment>
  );
  const simpleDataTableWithButtonsProps = {
    defaultExpLevel: 0,
    simpleDataTableProps: {
      table: content.beneficiaryTable,
      maxColWidth: 300,
      download: location.pathname,
      title: `${budgetSection} ${title}`,
      getSubComponent,
      renderSubComponent: true
    },
    treeTableButtonsProps: {
      a11yTitle: title,
      headerLevel: 2,
      buttons: [{
        info: 'View the capital agency\'s plan items grouped by beneficiary agency.',
        text: 'View by beneficiary agency',
        accessor: 'govLevelTwo',
        expLevel: 0
      }, {
        info: 'View all of the plan items under this capital agency.',
        text: 'View by plan item',
        accessor: 'planItems',
        expLevel: 1
      }]
    }
  };

  // metadata for the page
  const metaTitle = `${budgetSection}: ${title}`;
  const metaDescription = `Capital investment ${metaTitle.toLowerCase()}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        metaType: 'dataset',
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          {content.beneficiaryTable
            && <SimpleDataTableWithButtons {...simpleDataTableWithButtonsProps} />
          }
        </Section>
      </Layout>
    </Fragment>
  );
};

InvestmentSubject.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default InvestmentSubject;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    content: planInvestmentSubject(fields: { slug: { eq: $slug } }) {
      budgetSection
      title
      baseSectionURL
      beneficiaryTable {
        headers {
          name
          type
          align
          value
        }
        data {
          govLevelTwo
          govLevelOne
          govLevelOneDisplay
          govLevelTwoDisplay
          stateBondCap
          nonBondCap
          operatingFunds
          federalFunds
          otherFunds
          total
          planItems {
            suppressed
            planItemName
            planItemNumber
            govLevelOne
            govLevelTwo
            govLevelTwoDisplay
            data {
              stateBondCap
              nonBondCap
              operatingFunds
              federalFunds
              otherFunds
              total
            }
          }
        }
        total {
          govLevelTwo
          stateBondCap
          nonBondCap
          operatingFunds
          federalFunds
          otherFunds
          total
        }
      }
      # Get the last modified timestamp for the file powering the plan item pages
      ...InvestmentSubjectDataTimeStamp
    }
  }
`;
